import Robot from './robotbase'
import { Euler, Quaternion } from 'three'


class SimpleArm extends Robot {
	constructor() {
		super('SimpleArm', 'simple_arm_description', 'urdf/simple_arm.urdf.xacro');

		this.robotRoot = 'base_link';
		this.handRoot = 'gripper_base';

		this.passiveJointNames = ['swing_roll', 'swing_pitch'];
		this.passiveAnchor = 'wrist_link';
		this._passiveState = {
			roll: 0,
			pitch: 0,
			rollVelocity: 0,
			pitchVelocity: 0,
			lastTime: 0,
		};

		this.modelScale = 10;

		this.defaultPose = {
			joint_1: 0,
			joint_2: -0.8,
			joint_3: 1.15,
			joint_4: -0.35,
		};

		this.tcp.parent = 'tcp_link';
		this.tcp.position = [0, 0, 0];

		this.ikEnabled = [
			'joint_1',
			'joint_2',
			'joint_3',
			'joint_4',
		];

		this.interactionJointLimits = {
			joint_1: { lower: -Math.PI, upper: Math.PI },
			joint_2: { lower: -1.8, upper: 1.35 },
			joint_3: { lower: -2.1, upper: 2.1 },
			joint_4: { lower: -2.2, upper: 2.2 },
		};

		this.ikSettings = {
			maxIterations: 60,
			stopDistance: 0.02,
		};
	}

	init(model) {
		super.init(model);

		this.arm.joints = this.arm.joints.filter(joint => !this.passiveJointNames.includes(joint.name));
		this.arm.movable = this.arm.movable.filter(joint => !this.passiveJointNames.includes(joint.name));
		this.partNames.arm = this.partNames.arm.filter(name => !this.passiveJointNames.includes(name));

		this._passiveJoints = {
			roll: this.model.joints.swing_roll,
			pitch: this.model.joints.swing_pitch,
		};
		this._passiveState.lastTime = performance.now();
	}

	updatePassiveSuspension() {
		if (!this._passiveJoints || !this._passiveJoints.roll || !this._passiveJoints.pitch) {
			return false;
		}

		const now = performance.now();
		const dt = Math.min(0.05, Math.max(0.001, (now - this._passiveState.lastTime) / 1000));
		this._passiveState.lastTime = now;

		const anchor = this.getFrame(this.passiveAnchor);
		const anchorQuat = new Quaternion();
		anchor.getWorldQuaternion(anchorQuat);

		const anchorEuler = new Euler().setFromQuaternion(anchorQuat, 'XYZ');
		const targetRoll = this._clampAngle(-anchorEuler.x, this._passiveJoints.roll);
		const targetPitch = this._clampAngle(-anchorEuler.y, this._passiveJoints.pitch);

		const rollActive = this._stepPassiveAxis('roll', targetRoll, dt);
		const pitchActive = this._stepPassiveAxis('pitch', targetPitch, dt);

		return rollActive || pitchActive;
	}

	_stepPassiveAxis(axisName, targetAngle, dt) {
		const joint = this._passiveJoints[axisName];
		const stateName = axisName + 'Velocity';
		const currentAngle = joint.angle;
		const angleError = this._shortestAngleDelta(currentAngle, targetAngle);

		const stiffness = 34;
		const damping = 8;
		this._passiveState[stateName] += angleError * stiffness * dt;
		this._passiveState[stateName] *= Math.exp(-damping * dt);

		const nextAngle = this._clampAngle(currentAngle + this._passiveState[stateName] * dt, joint);
		joint.setJointValue(nextAngle);

		return Math.abs(angleError) > 0.01 || Math.abs(this._passiveState[stateName]) > 0.01;
	}

	_clampAngle(angle, joint) {
		return Math.min(joint.limit.upper, Math.max(joint.limit.lower, angle));
	}

	_shortestAngleDelta(from, to) {
		return Math.atan2(Math.sin(to - from), Math.cos(to - from));
	}
}

module.exports = new SimpleArm();